import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, ChevronLeft, ChevronRight, Eye, List, Plus } from "lucide-react";
import { api } from "@/lib/api/client";
import { downloadFile } from "@/lib/api/download";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { EntityPickerField, type PickedEntity } from "@/components/ui/EntityPicker";
import { RowActions } from "@/components/ui/RowActions";
import { ScreenToolbar } from "@/components/ui/ScreenToolbar";
import { cn } from "@/lib/utils";

/**
 * Quản lý lịch họp › Lịch họp (meetingWeek.zul).
 *
 * Hệ cũ là một màn duy nhất có 10 ô lọc và hai chế độ xem: **Danh sách** và
 * **Lịch** (Ngày / Tuần), kèm chú thích màu theo trạng thái. Bản trước của hệ mới
 * chỉ là danh sách phẳng 6 cột không có bộ lọc.
 */
type Meeting = {
  ID: number;
  TITLE: string | null;
  SUMARY: string | null;
  START_TIME: string | null;
  END_TIME: string | null;
  STATE: number | null;
  CONTACT: string | null;
  SHOW_PRESIDENT: string | null;
  OTHER_PARTICIPANT: string | null;
  CREATOR_NAME: string | null;
  ROOM_NAME: string | null;
};

/** Trạng thái và màu chú thích lấy đúng theo hệ cũ. */
const STATES = [
  { value: 1, label: "Chờ duyệt", dot: "bg-success", row: "bg-success/10" },
  { value: 2, label: "Đã duyệt", dot: "bg-primary", row: "bg-primary/10" },
  { value: 3, label: "Từ chối", dot: "bg-destructive", row: "bg-destructive/10" },
  { value: 4, label: "Hủy", dot: "bg-muted-foreground", row: "bg-muted" },
] as const;
const stateOf = (v: number | null) => STATES.find((s) => s.value === v);

const DAY_LABEL = ["CN", "Th 2", "Th 3", "Th 4", "Th 5", "Th 6", "Th 7"];
const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 07:00 → 18:00 như hệ cũ

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Thứ 2 của tuần chứa `d` — hệ cũ mở lịch tuần bắt đầu từ thứ 2. */
function startOfWeek(d: Date) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

type View = "list" | "calendar";
type Span = "day" | "week";

type Filters = {
  roomScope: string;
  room: string;
  recurrence: string;
  title: string;
  content: string;
  member: PickedEntity | null;
  otherParticipant: string;
  org: PickedEntity | null;
  state: string;
  searchBy: string;
};

/** Ô "Tìm kiếm theo" của hệ cũ — AppConstants.MEETING.SEARCH_BY. */
const SEARCH_BY = [
  { value: "-1", label: "Lịch họp của lãnh đạo" },
  { value: "-2", label: "Lịch họp của lãnh đạo và đơn vị" },
  { value: "-8", label: "Lịch họp tôi tạo" },
  { value: "-9", label: "Lịch họp tôi tham gia" },
];
const EMPTY: Filters = {
  roomScope: "",
  room: "",
  recurrence: "",
  title: "",
  content: "",
  member: null,
  otherParticipant: "",
  org: null,
  state: "",
  searchBy: "",
};

export function MeetingSchedulePage() {
  const nav = useNavigate();
  // Hệ cũ mở thẳng vào lưới tuần, không phải danh sách.
  const [view, setView] = useState<View>("calendar");
  const [span, setSpan] = useState<Span>("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);

  const days = useMemo(
    () =>
      span === "day"
        ? [anchor]
        : Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)),
    [anchor, span],
  );
  const rangeFrom = iso(days[0]);
  const rangeTo = iso(days[days.length - 1]);

  const { data: rooms } = useQuery({
    queryKey: ["meeting-rooms"],
    queryFn: async () => {
      const { data } = await api.get("/meeting/rooms");
      return (data.data ?? data) as { MEETING_RESOURCE_ID: number; NAME: string | null }[];
    },
  });

  const params = {
    title: applied.title || undefined,
    content: applied.content || undefined,
    otherParticipant: applied.otherParticipant || undefined,
    roomId: applied.room || undefined,
    roomScope: applied.roomScope || undefined,
    recurrence: applied.recurrence || undefined,
    state: applied.state || undefined,
    memberId: applied.member?.id,
    orgId: applied.org?.id,
    searchBy: applied.searchBy || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ["meeting-search", view, span, rangeFrom, rangeTo, page, pageSize, applied],
    queryFn: async () => {
      const { data } = await api.get("/meeting/search", {
        params:
          view === "calendar"
            ? { ...params, fromDate: rangeFrom, toDate: rangeTo, page: 1, pageSize: 500 }
            : { ...params, page, pageSize },
      });
      return (data.data ?? data) as { items: Meeting[]; total: number };
    },
  });

  /** "Xuất báo cáo" — hệ cũ có nút này ngay cạnh nút Tìm kiếm. */
  const onExport = () =>
    downloadFile(
      "/meeting/search/export",
      Object.fromEntries(
        Object.entries({ ...params, fromDate: rangeFrom, toDate: rangeTo }).filter(
          ([, v]) => v !== undefined && v !== null && v !== "",
        ),
      ) as Record<string, string>,
      "lich-hop.csv",
    );

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const columns: Column<Meeting>[] = [
    {
      key: "stt",
      header: "STT",
      width: "56px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "action",
      header: "Thao tác",
      width: "90px",
      align: "center",
      render: (r) => (
        <RowActions
          actions={[{ label: "Xem chi tiết", icon: Eye, onClick: () => nav(`/meetings/${r.ID}`) }]}
        />
      ),
    },
    {
      key: "title",
      header: "Chủ đề cuộc họp",
      render: (r) => <span className="font-medium">{r.TITLE ?? "—"}</span>,
    },
    { key: "room", header: "Phòng họp", width: "180px", render: (r) => r.ROOM_NAME ?? "—" },
    {
      key: "president",
      header: "Chủ trì",
      width: "180px",
      render: (r) => r.SHOW_PRESIDENT ?? "—",
    },
    {
      key: "start",
      header: "Bắt đầu",
      width: "150px",
      render: (r) => <span className="tabular-nums text-sm">{r.START_TIME ?? "—"}</span>,
    },
    {
      key: "end",
      header: "Kết thúc",
      width: "150px",
      render: (r) => <span className="tabular-nums text-sm">{r.END_TIME ?? "—"}</span>,
    },
    {
      key: "state",
      header: "Trạng thái",
      width: "130px",
      render: (r) => {
        const st = stateOf(r.STATE);
        return st ? (
          <span className="inline-flex items-center gap-1.5">
            <span className={cn("h-2 w-2 rounded-full", st.dot)} /> {st.label}
          </span>
        ) : (
          "—"
        );
      },
    },
  ];

  const inSlot = (m: Meeting, day: Date, hour: number) => {
    if (!m.START_TIME) return false;
    const st = new Date(m.START_TIME.replace(" ", "T"));
    return (
      st.getFullYear() === day.getFullYear() &&
      st.getMonth() === day.getMonth() &&
      st.getDate() === day.getDate() &&
      st.getHours() === hour
    );
  };

  return (
    <div>
      <PageHeader
        title="Lịch họp"
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} cuộc họp`}
        breadcrumb="Quản lý lịch họp › Lịch họp"
        actions={
          <ScreenToolbar
            actions={[
              {
                label: "Thêm mới",
                icon: Plus,
                altKey: "1",
                tone: "primary",
                onClick: () => nav("/meetings/new"),
              },
            ]}
          />
        }
      />

      <SearchPanel
        fields={[
          {
            label: "Chọn loại phòng",
            control: (
              <select
                className={filterInputClass}
                value={filters.roomScope}
                onChange={(e) => set("roomScope", e.target.value)}
              >
                <option value="">Tất cả các loại phòng</option>
                <option value="managed">Phòng họp được quản lý</option>
                <option value="unmanaged">Phòng họp không được quản lý</option>
              </select>
            ),
          },
          {
            label: "Chọn phòng họp",
            control: (
              <select
                className={filterInputClass}
                value={filters.room}
                onChange={(e) => set("room", e.target.value)}
              >
                <option value="">---- Tất cả ----</option>
                {(rooms ?? []).map((r) => (
                  <option key={r.MEETING_RESOURCE_ID} value={r.MEETING_RESOURCE_ID}>
                    {r.NAME ?? r.MEETING_RESOURCE_ID}
                  </option>
                ))}
              </select>
            ),
          },
          {
            label: "Lịch định kỳ",
            control: (
              <select
                className={filterInputClass}
                value={filters.recurrence}
                onChange={(e) => set("recurrence", e.target.value)}
              >
                <option value="">---- Chọn tất cả ----</option>
                <option value="DAILY">Hàng ngày</option>
                <option value="WEEKLY">Hàng tuần</option>
                <option value="MONTHLY">Hàng tháng</option>
              </select>
            ),
          },
          {
            label: "Chủ đề cuộc họp",
            control: (
              <input
                className={filterInputClass}
                value={filters.title}
                onChange={(e) => set("title", e.target.value)}
              />
            ),
          },
          {
            label: "Nội dung họp",
            control: (
              <input
                className={filterInputClass}
                value={filters.content}
                onChange={(e) => set("content", e.target.value)}
              />
            ),
          },
          {
            label: "Cá nhân tham gia",
            control: (
              <EntityPickerField
                kind="staff"
                value={filters.member}
                onChange={(v) => set("member", v)}
              />
            ),
          },
          {
            label: "Thành phần tham gia khác",
            control: (
              <input
                className={filterInputClass}
                value={filters.otherParticipant}
                onChange={(e) => set("otherParticipant", e.target.value)}
              />
            ),
          },
          {
            label: "Đơn vị tham gia",
            control: (
              <EntityPickerField kind="org" value={filters.org} onChange={(v) => set("org", v)} />
            ),
          },
          {
            label: "Tìm kiếm theo",
            control: (
              <select
                className={filterInputClass}
                value={filters.searchBy}
                onChange={(e) => set("searchBy", e.target.value)}
              >
                <option value="">---- Chọn tất cả ----</option>
                {SEARCH_BY.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ),
          },
          {
            label: "Trạng thái",
            control: (
              <select
                className={filterInputClass}
                value={filters.state}
                onChange={(e) => set("state", e.target.value)}
              >
                <option value="">---- Chọn tất cả ----</option>
                {STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            ),
          },
        ]}
        onSubmit={() => {
          setApplied(filters);
          setPage(1);
        }}
        onExport={onExport}
      />

      {/* Thanh chuyển chế độ + chú thích màu, đúng như hệ cũ */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <ToggleBtn active={view === "list"} onClick={() => setView("list")} icon={List}>
            Danh sách
          </ToggleBtn>
          <ToggleBtn
            active={view === "calendar"}
            onClick={() => setView("calendar")}
            icon={CalendarDays}
          >
            Lịch
          </ToggleBtn>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {STATES.map((s) => (
            <span key={s.value} className="inline-flex items-center gap-1.5 text-xs">
              <span className={cn("h-2.5 w-2.5 rounded-sm", s.dot)} />
              {s.label.toUpperCase()}
            </span>
          ))}
        </div>

        {view === "calendar" && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Kỳ trước"
              className="h-8 w-8 grid place-items-center rounded-sm border border-border hover:bg-muted"
              onClick={() => setAnchor((d) => addDays(d, span === "day" ? -1 : -7))}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-sm tabular-nums">
              {rangeFrom}
              {span === "week" ? ` → ${rangeTo}` : ""}
            </span>
            <button
              type="button"
              aria-label="Kỳ sau"
              className="h-8 w-8 grid place-items-center rounded-sm border border-border hover:bg-muted"
              onClick={() => setAnchor((d) => addDays(d, span === "day" ? 1 : 7))}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <ToggleBtn active={span === "day"} onClick={() => setSpan("day")}>
              Ngày
            </ToggleBtn>
            <ToggleBtn active={span === "week"} onClick={() => setSpan("week")}>
              Tuần
            </ToggleBtn>
          </div>
        )}
      </div>

      {view === "list" ? (
        <>
          <DataTable<Meeting>
            columns={columns}
            rows={data?.items ?? []}
            keyBy={(r) => r.ID}
            isLoading={isLoading}
            onRowClick={(r) => nav(`/meetings/${r.ID}`)}
            empty="Không có cuộc họp nào"
            dense
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border bg-background">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-muted/60 text-xxs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-20 border-b border-border h-10" />
                {days.map((d) => (
                  <th key={d.toISOString()} className="border-b border-l border-border h-10 px-2">
                    {DAY_LABEL[d.getDay()]} {String(d.getDate()).padStart(2, "0")}/
                    {String(d.getMonth() + 1).padStart(2, "0")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HOURS.map((h) => (
                <tr key={h}>
                  <td className="h-14 px-2 text-xs text-muted-foreground tabular-nums align-top border-b border-border">
                    {String(h).padStart(2, "0")}:00
                  </td>
                  {days.map((d) => (
                    <td
                      key={`${d.toISOString()}-${h}`}
                      className="h-14 border-b border-l border-border align-top p-1"
                    >
                      {(data?.items ?? [])
                        .filter((m) => inSlot(m, d, h))
                        .map((m) => {
                          const st = stateOf(m.STATE);
                          return (
                            <button
                              key={m.ID}
                              type="button"
                              onClick={() => nav(`/meetings/${m.ID}`)}
                              title={`${m.TITLE ?? ""} · ${m.ROOM_NAME ?? ""}`}
                              className={cn(
                                "block w-full text-left text-xs px-1.5 py-1 rounded-sm mb-1 truncate hover:underline",
                                st?.row ?? "bg-muted",
                              )}
                            >
                              {m.TITLE ?? "(Không tiêu đề)"}
                            </button>
                          );
                        })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ToggleBtn({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon?: typeof List;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border text-xs font-medium transition-colors",
        active
          ? "border-transparent bg-primary text-primary-foreground"
          : "border-border hover:bg-muted",
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}
